import React from 'react';
import Fuse from 'fuse.js';
import { useSelector } from 'react-redux';
import { cn } from '@/lib/utils';
import { isTouchViewport } from '@/hooks/use-mobile';
import {
	Search,
	Clock,
	Star,
	XCircle,
	SortAsc,
	Plus,
	Pencil,
	Rows3,
	Rows4,
	ArrowUpDown,
	Ellipsis,
	FolderInput,
	FolderX,
	MessageSquareText,
	Trash2
} from 'lucide-react';

import { Card, CardHeader, CardContent } from '@/components/ui/card';

import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@/components/ui/dialog';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { ActionSheet, Chip } from '@/components/mobile';
import CreateTemplateDialog from '../../templates/CreateTemplateDialog';
import FolderFilter from './FolderFilter';
import MoveToFolderMenu, {
	CreateFolderAndMoveDialog,
	useMoveTemplateToFolder
} from './MoveToFolderMenu';

const fuseOptions = {
	shouldSort: true,
	threshold: 0.4,
	keys: ['title', 'text']
};

// picking is high-frequency: remember the last folder per business + kind so
// repeat picks from a working folder cost zero extra clicks
const rememberedFilterKey = (businessId, kind) =>
	`templateFolder:${businessId}:${kind}`;

const getRememberedFilter = (businessId, kind) => {
	try {
		return window.localStorage.getItem(rememberedFilterKey(businessId, kind));
	} catch (err) {
		return null;
	}
};

const setRememberedFilter = (businessId, kind, filter) => {
	try {
		window.localStorage.setItem(rememberedFilterKey(businessId, kind), filter);
	} catch (err) {
		// localStorage unavailable (private mode etc.) — not worth surfacing
	}
};

// An open picker keeps its search and sort in this tab: crossing the phone
// breakpoint (rotating) remounts the composer's picker while it stays open.
const openPickerKey = (businessId, kind) =>
	`templatePicker:${businessId}:${kind}`;

const getOpenPickerState = (businessId, kind) => {
	try {
		return JSON.parse(
			window.sessionStorage.getItem(openPickerKey(businessId, kind))
		);
	} catch (err) {
		return null;
	}
};

const setOpenPickerState = (businessId, kind, state) => {
	try {
		if (state) {
			window.sessionStorage.setItem(
				openPickerKey(businessId, kind),
				JSON.stringify(state)
			);
		} else {
			window.sessionStorage.removeItem(openPickerKey(businessId, kind));
		}
	} catch (err) {
		// sessionStorage unavailable — a remount just starts fresh
	}
};

const matchesKind = (template, kind) =>
	kind === 'groupChat' ? Boolean(template.isGroupChat) : !template.isGroupChat;

// The ref offers createTemplate(), so a page can put "New template" in its
// phone navigation bar.
const TemplateSelector = React.forwardRef(({
	templates,
	onOpenChange = () => {},
	templatesExpanded,
	showEmojiPicker,
	toggleTemplates,
	handleTemplateClick,
	renderDialog,
	deleteTemplate,
	getUserTemplates,
	expandable = false,
	// 'sms' | 'groupChat' | 'all' — which kind of templates this surface shows.
	// The conversation picker and every legacy consumer use the 'sms' default;
	// the group-chat composer passes 'groupChat'; the manager passes 'all'.
	templateKind = 'sms',
	// when set, opening the picker lands on this filter instead of the
	// remembered chip (without persisting it) — ChatBox uses it so the
	// payment-request hoist is never hidden behind a folder chip
	filterOverride = null,
	// phones: the page's navigation bar has the New Template button, so the
	// search field gets the whole row
	mobileCreateInHeader = false,
	// medium widths (768-1279px): folder chips above the list instead of the
	// rail, for a surface that's already narrow there (Settings)
	foldersAsChipsBelowWide = false
}, ref) => {
	const templateFolders = useSelector((state) => state.templateFolders);
	const businessId = useSelector((state) => state.auth?._id);
	const isEnterpriseEnabled = useSelector((state) =>
		Boolean(state.auth?.user?.isEnterpriseEnabled)
	);

	// a picker that mounts already open was remounted, not opened
	const [restoredPicker] = React.useState(() =>
		renderDialog && templatesExpanded
			? getOpenPickerState(businessId, templateKind)
			: null
	);
	const [sortBy, setSortBy] = React.useState(
		restoredPicker?.sortBy || 'createdAt'
	); // createdAt or alphabetically
	const [searchTerm, setSearchTerm] = React.useState(
		restoredPicker?.searchTerm || ''
	);
	const [kindTab, setKindTab] = React.useState('sms'); // only used when templateKind === 'all'
	const [openCreateTemplateDialog, setOpenCreateTemplateDialog] =
		React.useState(false);
	const [selectedTemplate, setSelectedTemplate] = React.useState(null);
	const [isExpanded, setIsExpanded] = React.useState(expandable);
	// phones: the "…" sheet for one template, and what it opens
	const [actionsOpen, setActionsOpen] = React.useState(false);
	const [actionsTemplate, setActionsTemplate] = React.useState(null);
	const [sortSheetOpen, setSortSheetOpen] = React.useState(false);
	const [newFolderFor, setNewFolderFor] = React.useState(null);
	const [templateToDelete, setTemplateToDelete] = React.useState(null);
	// the card's "…" button, for focus when the delete confirm closes
	const deleteOpener = React.useRef(null);
	const listRef = React.useRef(null);
	const moveTemplate = useMoveTemplateToFolder(isEnterpriseEnabled);

	const effectiveKind = templateKind === 'all' ? kindTab : templateKind;

	// 'all' | 'favorites' | 'unfiled' | a folder _id
	const [activeFilter, setActiveFilter] = React.useState(
		() => getRememberedFilter(businessId, effectiveKind) || 'all'
	);

	const canManage = Boolean(deleteTemplate && getUserTemplates);

	const folderIdSet = React.useMemo(
		() => new Set(templateFolders.map((folder) => String(folder._id))),
		[templateFolders]
	);

	// a stored filter can go stale (folder deleted, kind tab switched)
	const validateFilter = React.useCallback(
		(filter) => {
			if (!filter) return 'all';
			if (['all', 'favorites', 'unfiled'].includes(filter)) return filter;
			return folderIdSet.has(filter) ? filter : 'all';
		},
		[folderIdSet]
	);

	React.useEffect(() => {
		setActiveFilter(
			validateFilter(getRememberedFilter(businessId, effectiveKind))
		);
	}, [businessId, effectiveKind, validateFilter]);

	React.useEffect(() => {
		if (templatesExpanded && filterOverride) {
			// display-only: deliberately not written to localStorage
			setActiveFilter(validateFilter(filterOverride));
		}
	}, [templatesExpanded, filterOverride, validateFilter]);

	const selectFilter = (filter) => {
		setActiveFilter(filter);
		setRememberedFilter(businessId, effectiveKind, filter);
	};

	const handleCloseCreateTemplateDialog = () => {
		setOpenCreateTemplateDialog(false);
		setSelectedTemplate(null);
	};

	const showCreateTemplateDialog = ({ template }) => {
		setOpenCreateTemplateDialog(true);
		setSelectedTemplate(template);
	};

	React.useImperativeHandle(ref, () => ({
		createTemplate: () => showCreateTemplateDialog({ template: null })
	}));

	// enterprise mode aggregates other businesses' templates, but folders
	// (and moves) are active-business only
	const isOwnTemplate = (template) =>
		canManage && String(template._business) === String(businessId);

	const openTemplateActions = (template) => {
		setActionsTemplate(template);
		setActionsOpen(true);
	};

	const confirmDeleteTemplate = () => {
		const template = templateToDelete;
		setTemplateToDelete(null);
		deleteTemplate({ template });
	};

	const kindTemplates = React.useMemo(
		() =>
			(templates || []).filter((template) =>
				matchesKind(template, effectiveKind)
			),
		[templates, effectiveKind]
	);

	const isUnfiled = React.useCallback(
		(template) =>
			// enterprise-safe: templates whose folder belongs to another
			// business bucket into Unfiled instead of vanishing
			!template._folder || !folderIdSet.has(String(template._folder)),
		[folderIdSet]
	);

	const counts = React.useMemo(() => {
		const byFolder = {};
		let favorites = 0;
		let unfiled = 0;

		kindTemplates.forEach((template) => {
			if (template.isFavorite) favorites += 1;
			if (isUnfiled(template)) {
				unfiled += 1;
			} else {
				const id = String(template._folder);
				byFolder[id] = (byFolder[id] || 0) + 1;
			}
		});

		return { all: kindTemplates.length, favorites, unfiled, byFolder };
	}, [kindTemplates, isUnfiled]);

	// a remembered 'unfiled' chip can go stale (everything got filed) — the
	// chip wouldn't render, leaving an invisible filter over an empty list
	const resolvedFilter =
		activeFilter === 'unfiled' && counts.unfiled === 0 ? 'all' : activeFilter;

	const searchedTemplates = React.useMemo(() => {
		if (!searchTerm) return null;
		// search spans every folder within the current kind — it stays the
		// fastest picking path and must never be narrowed by the active chip
		const fuse = new Fuse(kindTemplates, fuseOptions);
		return fuse.search(searchTerm).map((result) => result.item);
	}, [searchTerm, kindTemplates]);

	const filteredTemplates = React.useMemo(() => {
		if (searchedTemplates) return searchedTemplates;

		switch (resolvedFilter) {
			case 'all':
				return kindTemplates;
			case 'favorites':
				return kindTemplates.filter((t) => t.isFavorite);
			case 'unfiled':
				return kindTemplates.filter(isUnfiled);
			default:
				return kindTemplates.filter(
					(t) => String(t._folder) === resolvedFilter
				);
		}
	}, [kindTemplates, searchedTemplates, resolvedFilter, isUnfiled]);

	const sortTemplates = (templatesToSort) => {
		if (!templatesToSort) return [];

		const favorites = templatesToSort.filter((x) => x.isFavorite);
		const notFavorites = templatesToSort.filter((x) => !x.isFavorite);

		if (sortBy === 'alphabetically') {
			return [
				...favorites.sort((a, b) =>
					(a.title && a.title.toLowerCase()) <
					(b.title && b.title.toLowerCase())
						? -1
						: 1
				),
				...notFavorites.sort((a, b) =>
					(a.title && a.title.toLowerCase()) <
					(b.title && b.title.toLowerCase())
						? -1
						: 1
				)
			];
		}

		return [
			...favorites.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1)),
			...notFavorites.sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
		];
	};

	React.useEffect(() => {
		if (!templatesExpanded) {
			setTimeout(() => {
				// reset search and sort, but keep the remembered folder chip —
				// the whole point is that reopening lands on the working folder
				setSearchTerm('');
				setSortBy('createdAt');
			}, 500);
		}
	}, [templatesExpanded]);

	// remember an open picker's search and sort; forget them when it closes
	const wasExpanded = React.useRef(templatesExpanded);
	React.useEffect(() => {
		if (!renderDialog) return;
		if (templatesExpanded) {
			setOpenPickerState(businessId, templateKind, { searchTerm, sortBy });
		} else if (wasExpanded.current) {
			setOpenPickerState(businessId, templateKind, null);
		}
		wasExpanded.current = templatesExpanded;
	}, [renderDialog, templatesExpanded, businessId, templateKind, searchTerm, sortBy]);

	const handleClearSearch = () => {
		setSearchTerm('');
	};

	const renderEmptyState = () => {
		if (searchTerm) {
			return (
				<div className="flex flex-col items-center justify-center py-12 text-center">
					<div className="text-muted-foreground mb-2">
						No templates found
					</div>
					<Button variant="outline" size="sm" onClick={handleClearSearch}>
						Clear search
					</Button>
				</div>
			);
		}

		const isFolderFilter = !['all', 'favorites', 'unfiled'].includes(
			resolvedFilter
		);
		const emptyCopy = isFolderFilter
			? 'No templates in this folder yet'
			: effectiveKind === 'groupChat'
				? 'No group chat templates yet'
				: 'No templates yet';

		return (
			<div className="flex flex-col items-center justify-center py-12 text-center gap-3">
				<div className="text-muted-foreground">{emptyCopy}</div>
				{effectiveKind === 'groupChat' && !isFolderFilter && (
					<p className="text-xs text-muted-foreground max-w-sm">
						Group chat templates are reusable messages for your
						internal team chats.
					</p>
				)}
				{canManage && (
					<Button
						size="sm"
						className="gap-2"
						onClick={() => showCreateTemplateDialog({ template: null })}
					>
						<Plus className="h-4 w-4" />
						Create template
					</Button>
				)}
			</div>
		);
	};

	const templateList = (
		<div ref={listRef} className="grid gap-3 min-w-0">
			{sortTemplates(filteredTemplates).map((template) => (
				<div
					key={template._id}
					className="w-full min-w-0 text-left group relative"
				>
					<button
						type="button"
						onClick={() => {
							if (handleTemplateClick) {
								handleTemplateClick({ template });
							} else if (canManage) {
								// In TemplatesDashboard where there's no handleTemplateClick,
								// clicking the card should open the edit dialog
								showCreateTemplateDialog({ template });
							}
						}}
						className="w-full min-w-0 text-left"
					>
						<Card className="relative border border-border hover:border-primary/20 hover:bg-accent/50 transition-all duration-200 overflow-hidden max-md:rounded-xl max-md:active:bg-accent/50">
							<div className="p-3 pr-28 max-md:pr-12">
								{/* phones: these move into the "…" sheet */}
								<div className="absolute top-3 right-3 flex flex-col items-end gap-1 max-md:hidden">
									<div className="flex items-center gap-1">
										{isOwnTemplate(template) && (
												<MoveToFolderMenu
													template={template}
													isEnterpriseRequested={
														isEnterpriseEnabled
													}
												/>
											)}
										{handleTemplateClick && canManage && (
											<Button
												onClick={(e) => {
													e.stopPropagation();
													showCreateTemplateDialog({
														template
													});
												}}
												variant="outline"
												size="sm"
												className="h-8 px-2 gap-1 hover:text-foreground max-md:size-11 max-md:px-0"
												aria-label="Edit template"
												title="Edit template"
											>
												<span className="text-xs font-medium max-md:sr-only">
													Edit
												</span>
												<Pencil className="h-3.5 w-3.5" />
											</Button>
										)}
									</div>
									<p className="text-[11px] font-normal">
										{template.text.length} chars
									</p>
								</div>
								<div className="flex items-center gap-2 mb-1.5">
									{template.isFavorite && (
										<Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400 shrink-0" />
									)}
									<h3 className="font-medium text-sm max-md:text-[15px]">
										{template.title}
									</h3>
									{templateKind === 'all' &&
										template.isGroupChat && (
											<Badge
												variant="secondary"
												className="text-[10px] px-1.5 py-0 h-4"
											>
												Group
											</Badge>
										)}
								</div>
								<p
									className={cn(
										'whitespace-pre-wrap break-words text-sm text-muted-foreground group-hover:text-foreground transition-colors',
										!isExpanded && 'line-clamp-3'
									)}
								>
									{template.text}
								</p>
								<p className="mt-1.5 text-xs text-muted-foreground md:hidden">
									{template.text.length} chars
								</p>
							</div>
						</Card>
					</button>
					{canManage && (
						<button
							type="button"
							onClick={() => openTemplateActions(template)}
							data-template-actions={template._id}
							aria-label={`More actions for ${template.title}`}
							aria-haspopup="dialog"
							className="absolute right-0.5 top-0.5 inline-flex size-11 items-center justify-center rounded-full text-muted-foreground active:bg-accent md:hidden"
						>
							<Ellipsis className="size-5" />
						</button>
					)}
				</div>
			))}
		</div>
	);

	// in the dialog and the manager the picker owns a bounded box and scrolls
	// inside it; the legacy inline surface still sizes off the viewport
	const fillsAvailableHeight = expandable || renderDialog;

	const content = (
		<Card
			// no overflow-hidden here: it would clip the focus ring of the
			// controls sitting flush against the top of the header
			className={cn(
				'w-full min-w-0 border-0 shadow-none',
				fillsAvailableHeight && 'flex min-h-0 grow flex-col',
				expandable && 'h-full',
				expandable &&
					!renderDialog &&
					'max-md:h-auto max-xl:[@media(max-height:560px)]:h-auto'
			)}
		>
			{/* inside the dialog the Card sits in DialogContent's own padding, so
			it drops its horizontal padding rather than inset everything twice */}
			<CardHeader
				className={cn(
					'pb-3 shrink-0',
					renderDialog && 'px-0 pt-0 max-xl:[@media(max-height:560px)]:pb-2',
					!renderDialog && 'max-md:px-4 max-md:pt-2'
				)}
			>
				<div
					className={cn(
						'flex flex-col space-y-3 min-w-0',
						// a phone held sideways: search and sort share one row
						renderDialog &&
							'max-xl:[@media(max-height:560px)]:flex-row max-xl:[@media(max-height:560px)]:flex-wrap max-xl:[@media(max-height:560px)]:items-center max-xl:[@media(max-height:560px)]:gap-2 max-xl:[@media(max-height:560px)]:space-y-0'
					)}
				>
					{templateKind === 'all' && (
						<Tabs value={kindTab} onValueChange={setKindTab}>
							<TabsList className="max-md:h-11 max-md:w-full max-md:p-0.5 max-md:[&>[role=tab]]:h-full max-md:[&>[role=tab]]:flex-1">
								<TabsTrigger value="sms">SMS Templates</TabsTrigger>
								<TabsTrigger value="groupChat">
									Group Chat
								</TabsTrigger>
							</TabsList>
						</Tabs>
					)}

					{/* Search bar and New Template button */}
					<div
						className={cn(
							'flex items-center gap-3 min-w-0',
							renderDialog &&
								'max-xl:[@media(max-height:560px)]:min-w-[16rem] max-xl:[@media(max-height:560px)]:flex-1'
						)}
					>
						<div className="relative flex-1 min-w-0">
							<div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-muted-foreground">
								<Search className="h-4 w-4" />
							</div>
							<Input
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder="Search templates..."
								className="h-10 w-full pl-10 pr-10 md:text-base max-md:h-11 max-md:rounded-[10px] max-md:border-transparent max-md:bg-zinc-200/70 max-md:shadow-none dark:max-md:bg-zinc-800"
								// Touch screens (phones either way up, tablets): no keyboard
								// over a picker the person has only just opened.
								autoFocus={!isTouchViewport()}
								// a text input, so Escape doesn't clear it on desktop
								inputMode="search"
								enterKeyHint="search"
								autoComplete="off"
							/>
							{searchTerm && (
								<button
									type="button"
									onClick={handleClearSearch}
									aria-label="Clear search"
									className="absolute inset-y-0 right-0 flex items-center pr-3 text-muted-foreground hover:text-foreground transition-colors max-md:w-11 max-md:justify-center max-md:pr-0"
								>
									<XCircle className="h-4 w-4" />
								</button>
							)}
						</div>
						{canManage && (
							<Button
								onClick={() =>
									showCreateTemplateDialog({ template: null })
								}
								size="default"
								className={cn(
									'h-10 gap-2 shrink-0 max-md:size-11 max-md:px-0',
									mobileCreateInHeader && 'max-md:hidden'
								)}
								aria-label="New Template"
							>
								<Plus className="h-4 w-4" />
								<span className="max-md:sr-only">New Template</span>
							</Button>
						)}
					</div>

					{/* phones: a Sort chip at the start of the folder chips instead */}
					<div className="flex flex-wrap items-center justify-end gap-2 max-md:hidden">
						{expandable && (
							<Button
								onClick={() => setIsExpanded((prev) => !prev)}
								variant={isExpanded ? 'default' : 'outline'}
								size="sm"
								className="gap-1.5"
							>
								{isExpanded ? (
									<Rows3 className="h-4 w-4" />
								) : (
									<Rows4 className="h-4 w-4" />
								)}
								<span className="hidden sm:inline">
									{isExpanded ? 'Compact' : 'Expanded'}
								</span>
							</Button>
						)}
						<Button
							onClick={() => setSortBy('alphabetically')}
							variant={
								sortBy === 'alphabetically' ? 'default' : 'outline'
							}
							size="sm"
							className="gap-1.5"
						>
							<SortAsc className="h-4 w-4" />
							<span className="hidden sm:inline">
								Sort alphabetically
							</span>
							<span className="sm:hidden">A-Z</span>
						</Button>

						<Button
							onClick={() => setSortBy('createdAt')}
							variant={
								sortBy === 'createdAt' ? 'default' : 'outline'
							}
							size="sm"
							className="gap-1.5"
						>
							<Clock className="h-4 w-4" />
							<span className="hidden sm:inline">
								Sort by newest
							</span>
							<span className="sm:hidden">Newest</span>
						</Button>
					</div>

					{searchTerm && resolvedFilter !== 'all' && (
						<p className="text-xs text-muted-foreground max-xl:[@media(max-height:560px)]:basis-full">
							Searching all folders
						</p>
					)}
				</div>
			</CardHeader>

			<CardContent
				className={cn(
					'flex w-full min-w-0 flex-col gap-3 md:flex-row md:gap-3',
					foldersAsChipsBelowWide && 'md:max-xl:!flex-col',
					renderDialog && 'px-0',
					// pages on phones and short screens scroll as a whole (the
					// page owns the scroller)
					!renderDialog &&
						fillsAvailableHeight &&
						'max-md:flex-none max-md:overflow-visible max-md:px-4 max-xl:[@media(max-height:560px)]:flex-none max-xl:[@media(max-height:560px)]:overflow-visible',
					renderDialog &&
						'max-xl:[@media(max-height:560px)]:!flex-col max-xl:[@media(max-height:560px)]:!gap-1',
					fillsAvailableHeight
						? 'min-h-0 flex-1 overflow-hidden'
						: 'h-[calc(100vh-440px)]'
				)}
			>
				<FolderFilter
					folders={templateFolders}
					counts={counts}
					activeFilter={resolvedFilter}
					onSelect={selectFilter}
					canManage={canManage}
					isEnterpriseRequested={isEnterpriseEnabled}
					chipsOnShortScreens={renderDialog}
					chipsBelowWide={foldersAsChipsBelowWide}
					leadingChips={
						<>
							<Chip
								icon={ArrowUpDown}
								onClick={() => setSortSheetOpen(true)}
								aria-haspopup="dialog"
								aria-label={`Sort: ${sortBy === 'alphabetically' ? 'A to Z' : 'Newest first'}`}
								className="md:hidden"
							>
								{sortBy === 'alphabetically' ? 'A–Z' : 'Newest'}
							</Chip>
							<span aria-hidden className="h-5 w-px shrink-0 bg-border md:hidden" />
						</>
					}
				/>

				<div className="min-h-0 min-w-0 flex-1">
					{filteredTemplates.length === 0 ? (
						<div className="flex h-full items-center justify-center">
							{renderEmptyState()}
						</div>
					) : (
						<ScrollArea
							className={cn(
								'h-full pr-3 max-md:pr-0',
								!renderDialog &&
									fillsAvailableHeight &&
									'max-md:h-auto max-xl:[@media(max-height:560px)]:h-auto'
							)}
						>
							{templateList}
						</ScrollArea>
					)}
				</div>
			</CardContent>
		</Card>
	);

	const renderCreateTemplateDialog = () => {
		if (!openCreateTemplateDialog) {
			return null;
		}

		const isFolderFilter = !['all', 'favorites', 'unfiled'].includes(
			resolvedFilter
		);

		return (
			<CreateTemplateDialog
				open={openCreateTemplateDialog}
				handleCloseCreateTemplateDialog={handleCloseCreateTemplateDialog}
				deleteTemplate={deleteTemplate}
				selectedTemplate={selectedTemplate}
				getUserTemplates={getUserTemplates}
				defaultIsGroupChat={effectiveKind === 'groupChat'}
				defaultFolderId={isFolderFilter ? activeFilter : null}
			/>
		);
	};

	// Phones: the card "…" sheet (the desktop card buttons, plus Delete), the
	// sort sheet, and what they open. Outside the picker dialog, so picking
	// "Use template" can close the picker without unmounting them.
	const renderPhoneSheets = () => {
		const template = actionsTemplate;
		const ownTemplate = Boolean(template) && isOwnTemplate(template);
		const currentFolderId =
			template?._folder && folderIdSet.has(String(template._folder))
				? String(template._folder)
				: null;
		const currentFolder = templateFolders.find(
			(folder) => String(folder._id) === currentFolderId
		);

		return (
			<>
				{template && (
					<ActionSheet
						open={actionsOpen}
						onOpenChange={setActionsOpen}
						title={template.title}
						items={[
							handleTemplateClick && {
								id: 'use',
								label: 'Use template',
								icon: MessageSquareText,
								afterClose: true,
								onSelect: () => handleTemplateClick({ template })
							},
							{
								id: 'edit',
								label: 'Edit template',
								icon: Pencil,
								afterClose: true,
								onSelect: () => showCreateTemplateDialog({ template })
							},
							ownTemplate && {
								id: 'move',
								label: 'Move to folder',
								icon: FolderInput,
								page: 'move',
								trailing: currentFolder?.title
							},
							ownTemplate && {
								id: 'delete',
								label: 'Delete template',
								icon: Trash2,
								destructive: true,
								afterClose: true,
								onSelect: () => {
									deleteOpener.current = listRef.current?.querySelector(
										`[data-template-actions="${template._id}"]`
									);
									setTemplateToDelete(template);
								}
							}
						].filter(Boolean)}
						pages={
							ownTemplate
								? [
										{
											id: 'move',
											title: 'Move to folder',
											searchable: templateFolders.length > 8,
											searchPlaceholder: 'Search folders',
											sections: [
												{
													id: 'folders',
													items: templateFolders.map((folder) => ({
														id: String(folder._id),
														label: folder.title,
														checked: currentFolderId === String(folder._id),
														onSelect: () => moveTemplate(template, folder._id)
													}))
												},
												{
													id: 'more',
													items: [
														currentFolderId && {
															id: 'remove',
															label: 'Remove from folder',
															icon: FolderX,
															onSelect: () => moveTemplate(template, null)
														},
														{
															id: 'new',
															label: 'New folder…',
															icon: Plus,
															afterClose: true,
															onSelect: () => setNewFolderFor(template)
														}
													].filter(Boolean)
												}
											].filter((section) => section.items.length > 0)
										}
									]
								: []
						}
					/>
				)}
				<ActionSheet
					open={sortSheetOpen}
					onOpenChange={setSortSheetOpen}
					title="Sort templates"
					sections={[
						{
							id: 'sort',
							items: [
								{
									id: 'createdAt',
									label: 'Newest first',
									icon: Clock,
									checked: sortBy === 'createdAt',
									onSelect: () => setSortBy('createdAt')
								},
								{
									id: 'alphabetically',
									label: 'A to Z',
									icon: SortAsc,
									checked: sortBy === 'alphabetically',
									onSelect: () => setSortBy('alphabetically')
								}
							]
						},
						...(expandable
							? [
									{
										id: 'view',
										items: [
											{
												id: 'expanded',
												label: 'Show full messages',
												icon: isExpanded ? Rows3 : Rows4,
												checked: isExpanded,
												onSelect: () => setIsExpanded((prev) => !prev)
											}
										]
									}
								]
							: [])
					]}
				/>
				{newFolderFor && (
					<CreateFolderAndMoveDialog
						template={newFolderFor}
						open
						onOpenChange={(open) => !open && setNewFolderFor(null)}
						isEnterpriseRequested={isEnterpriseEnabled}
					/>
				)}
				<AlertDialog
					open={Boolean(templateToDelete)}
					onOpenChange={(open) => !open && setTemplateToDelete(null)}
				>
					<AlertDialogContent
						// no Radix trigger: hand focus back to the card's "…" (gone
						// once the template is deleted)
						onCloseAutoFocus={(event) => {
							const opener = deleteOpener.current;
							deleteOpener.current = null;
							if (opener?.isConnected) {
								event.preventDefault();
								opener.focus({ preventScroll: true });
							}
						}}
					>
						<AlertDialogHeader>
							<AlertDialogTitle>
								Delete "{templateToDelete?.title}"?
							</AlertDialogTitle>
							<AlertDialogDescription>
								It's deleted for your whole team. This can't be undone.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>Cancel</AlertDialogCancel>
							<AlertDialogAction
								className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
								onClick={confirmDeleteTemplate}
							>
								Delete template
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</>
		);
	};

	if (renderDialog) {
		return (
			<>
				{renderCreateTemplateDialog()}
				{canManage && renderPhoneSheets()}
				<Dialog
					open={templatesExpanded}
					timeout={showEmojiPicker ? 0 : 'auto'}
					onOpenChange={onOpenChange}
					className="max-w-[950px]"
				>
					{/* fixed height, not min/max: the picker must not resize as
					folders with different template counts are selected */}
					<DialogContent
						mobileVariant="fullscreen"
						className="max-w-[900px] h-[min(760px,85vh)] w-[calc(100vw-2rem)] min-w-0 overflow-hidden flex flex-col p-6 sm:rounded-xl max-md:gap-3 max-md:overflow-hidden max-md:px-4 max-md:pb-[max(0.75rem,var(--safe-bottom))] max-md:pt-[max(0.75rem,var(--safe-top))] max-xl:[@media(max-height:560px)]:h-[calc(var(--app-height)-2rem)] max-xl:[@media(max-height:560px)]:gap-2 max-xl:[@media(max-height:560px)]:!overflow-hidden max-xl:[@media(max-height:560px)]:p-4"
					>
						<DialogHeader className="shrink-0">
							<DialogTitle className="text-xl">
								Select a Template
							</DialogTitle>
						</DialogHeader>
						<div className="min-h-0 min-w-0 grow flex flex-col">
							{content}
						</div>
						{/* phones: the header's close button and the search row's New
						Template button already cover this footer */}
						<DialogFooter className="flex justify-between items-center shrink-0 max-md:hidden max-xl:[@media(max-height:560px)]:hidden">
							<div className="flex gap-2">
								{canManage && (
									<Button
										onClick={() => {
											showCreateTemplateDialog({
												template: null
											});
										}}
										variant="default"
										className="gap-2"
									>
										<Plus className="h-4 w-4" />
										New Template
									</Button>
								)}
							</div>
							<Button variant="outline" onClick={toggleTemplates}>
								Close
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</>
		);
	}

	return (
		<>
			{renderCreateTemplateDialog()}
			{canManage && renderPhoneSheets()}
			<div
				className={cn(
					'w-full',
					expandable &&
						'h-full max-md:h-auto max-xl:[@media(max-height:560px)]:h-auto'
				)}
			>
				{content}
			</div>
		</>
	);
});
TemplateSelector.displayName = 'TemplateSelector';

export default TemplateSelector;
